// 土地の頁 — いまの町字（逆ジオコーダの muniCd / lv01Nm）から、江戸の村を引く判定。
// /tochi（TochiPage）と玄関（HomePage の TochiGlimpse）で同じ判定を使う。

export const townKey = (t) => (t || "").replace(/[0-9０-９一二三四五六七八九十]+丁目$/, "");

// 町名が江戸の村名を継いでいない（村の一部に付いた新しい町名／小名）か、村そのものが改名したか
const CARVED = ["wiki", "fudoki", "official", "ndl", "book", "record", "subdivision", "partial", "inferred"];

const EMPTY = {
  candidates: [],
  placeKey: null,
  chomeKey: null,
  fromMuni: false,
  basis: null,
  pending: null,
  carved: false,
  partial: null,
  caution: null,
  nowTown: null,
  here: "",
  source: null,
};

/**
 * @param {object} data   index.json
 * @param {object} place  { muniCd, lv01Nm }（lv01Nm は町字なしなら ""）
 * @param {string} forced /tochi?v=村ID で村を直接指定したとき
 */
export function resolvePlace(data, place, forced = null) {
  if (!data) return null;
  if (forced) return { ...EMPTY, candidates: data.villages?.[forced] ? [forced] : [] };
  if (!place) return { ...EMPTY };

  const chomeKey = `${place.muniCd}|${place.lv01Nm}`;
  const placeKey = `${place.muniCd}|${townKey(place.lv01Nm)}`;
  const byChome = data.index_chome?.[chomeKey];
  const byTown = data.index?.[placeKey];
  const byMuni = data.index_muni?.[place.muniCd];
  // 丁目ごとに違うもの（雪ノ下五丁目は浄妙寺村）を先に見て、無ければ町字全体、無ければ市区町村の全域が一つの村の所
  const candidates = byChome || byTown || byMuni || [];
  const fromMuni = !byChome && !byTown && !!byMuni;
  const basis = fromMuni && candidates.length > 1 ? "muni_merged" : data.index_basis?.[placeKey] ?? null;
  const muniName = data.munis?.[place.muniCd] || "";
  return {
    candidates,
    placeKey,
    chomeKey,
    fromMuni,
    basis,
    // 江戸のどの村だったか、記録が見つかっていない町字。推定はしない
    pending: data.pending?.[placeKey] ?? null,
    carved: CARVED.includes(basis),
    partial: data.index_partial?.[placeKey] ?? null,
    caution: data.index_caution?.[placeKey] ?? null,
    nowTown: townKey(place.lv01Nm) || (fromMuni ? muniName.replace(/^.+郡/, "") : null),
    here: `${muniName} ${place.lv01Nm}`,
    source: data.index_source?.[chomeKey] || data.index_source?.[placeKey] || null,
  };
}

/** 逆ジオコーダ（国土地理院）の結果を place に直す。大字の無い所（道志村など）は町字が「−」で返るので "" にする */
export function placeFromGsi(json) {
  const r = json?.results;
  if (!r) return null;
  const lv = /^[-−－‐]?$/.test((r.lv01Nm || "").trim()) ? "" : r.lv01Nm;
  // 北海道は muniCd が「1101」のように頭の 0 が落ちて返ることがあるので、5 桁にそろえる（索引は「01101」。2026-11-02）
  const muniCd = r.muniCd ? String(r.muniCd).padStart(5, "0") : r.muniCd;
  return { muniCd, lv01Nm: lv };
}

/** 市区町村ごとの小さな索引 c/<コード>.json に、この町字の頁があるか */
export function isCovered(cov, place) {
  if (!cov || !place) return false;
  const t = townKey(place.lv01Nm);
  return !!(
    (place.lv01Nm && cov.c?.includes(place.lv01Nm)) ||
    (t && cov.t?.includes(t)) ||
    cov.m ||
    (t && cov.u?.includes(t))
  );
}
